import type { ComponentProps } from "react";
import type { Badge } from "@/components/ui/badge";
import type { RawCreatorStatus } from "./types";

export function fmtNum(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString();
}

export function fmtClock(at: string | number | null | undefined): string {
  return at
    ? new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";
}

export function fmtDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Whole percent, or null when there is nothing to divide by. */
export function pct(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 100) : null;
}

/** "no_speech" → "No speech". */
export function humanize(value: string): string {
  const text = value.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type BadgeVariant = ComponentProps<typeof Badge>["variant"];

export const CREATOR_STATUS_ORDER: RawCreatorStatus[] = [
  "PENDING",
  "ACCEPTED",
  "REJECTED",
  "ERRORED",
  "DUPLICATE",
];

export const CREATOR_STATUS_BADGE: Record<RawCreatorStatus, BadgeVariant> = {
  PENDING: "ghost",
  ACCEPTED: "success",
  REJECTED: "error",
  ERRORED: "warning",
  DUPLICATE: "outline",
};

/** Segment colors of the analysis bar — PENDING is the empty track. */
export const CREATOR_STATUS_FILL: Record<RawCreatorStatus, string> = {
  PENDING: "bg-transparent",
  ACCEPTED: "bg-success",
  REJECTED: "bg-destructive/70",
  ERRORED: "bg-warning",
  DUPLICATE: "bg-muted-foreground/40",
};

/** Legend dots next to the counts — the bar's colors, with PENDING as the track's tint. */
export const CREATOR_STATUS_DOT: Record<RawCreatorStatus, string> = {
  ...CREATOR_STATUS_FILL,
  PENDING: "bg-primary/25",
};
